"use client";

import React, { useState, useEffect, useRef } from 'react';
import Image from 'next/image';
import { motion, AnimatePresence } from 'framer-motion';
import { Quote } from 'lucide-react';

// Define the Testimonial interface for better type safety
export interface Testimonial {
  id: number;
  quote: string;
  name: string;
  position: string;
  company: string;
  image: string;
}

interface TestimonialDisplayProps {
  testimonials: Testimonial[]; // Required now, no default
  darkMode?: boolean;
  accentColor?: string;
  backgroundColor?: string;
  showQuoteSymbol?: boolean;
  autoPlaySpeed?: number;
  className?: string;
}

const TestimonialDisplay: React.FC<TestimonialDisplayProps> = ({
  testimonials,
  darkMode = true,
  accentColor = "#3b82f6", // blue by default
  backgroundColor,
  showQuoteSymbol = true,
  autoPlaySpeed = 5000,
  className = ""
}) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [direction, setDirection] = useState(0);
  const [isHovered, setIsHovered] = useState(false);
  const autoPlayRef = useRef<NodeJS.Timeout | null>(null);

  // Stop autoplay function
  const stopAutoplay = () => {
    if (autoPlayRef.current) {
      clearInterval(autoPlayRef.current);
      autoPlayRef.current = null;
    }
  };

  // Setup autoplay and cleanup on unmount
  useEffect(() => {
    // Start autoplay function moved inside the useEffect
    const startAutoplay = () => {
      if (autoPlayRef.current) clearInterval(autoPlayRef.current);
      
      autoPlayRef.current = setInterval(() => {
        setDirection(1);
        setCurrentIndex(prev => (prev + 1) % testimonials.length);
      }, autoPlaySpeed);
    };
    
    if (!isHovered) {
      startAutoplay();
    }
    return () => stopAutoplay();
  }, [isHovered, autoPlaySpeed, testimonials.length]);

  // Handle navigation
  const handlePrev = () => {
    stopAutoplay();
    setDirection(-1);
    setCurrentIndex(prev => (prev - 1 + testimonials.length) % testimonials.length);
  };

  const handleNext = () => {
    stopAutoplay();
    setDirection(1);
    setCurrentIndex(prev => (prev + 1) % testimonials.length);
  };

  // Generate theme classes based on dark mode
  const textColor = darkMode ? 'text-white' : 'text-gray-900';
  const textSecondaryColor = darkMode ? 'text-gray-300' : 'text-gray-600';
  const navBtnBg = darkMode ? 'bg-gray-800/80' : 'bg-white/90';
  const navBtnColor = darkMode ? 'text-gray-200' : 'text-gray-700';
  
  // Container background style
  const containerStyle = backgroundColor ? { backgroundColor } : {};

  // Animation variants - Updated for Vertical Slide + Fade effect
  const transitionEffect = {
    opacity: { duration: 0.5, ease: "easeInOut" },
    y: { duration: 0.5, ease: "easeInOut" }
  };

  const slideVariants = {
    enter: {
      opacity: 0,
      y: 20 // Start below
    },
    center: {
      opacity: 1,
      y: 0 // Arrive at center
    },
    exit: {
      opacity: 0,
      y: -20 // Exit upwards
    }
  };

  return (
    <div 
      className={`w-full py-8 overflow-hidden ${className}`}
      style={containerStyle}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <div className="max-w-4xl mx-auto px-4 sm:px-6">
        <div className="relative">
          {/* Testimonial Content */}
          <div className="min-h-[500px] md:min-h-[280px] relative px-6 sm:px-16">
            <AnimatePresence initial={false} mode="wait" custom={direction}>
              <motion.div
                key={testimonials[currentIndex].id}
                custom={direction}
                variants={slideVariants}
                initial="enter"
                animate="center"
                exit="exit"
                transition={transitionEffect}
                className={`absolute inset-0 rounded-xl p-6 md:p-10`}
              >
                <div className="relative">
                  {/* Opening quote */}
                  {showQuoteSymbol && (
                    <div className="absolute -top-6 -left-2 opacity-40">
                      <Quote 
                        size={40} 
                        color={accentColor}
                      />
                    </div>
                  )}
                  
                  {/* Testimonial text */}
                  <div className={`font-serif text-xl md:text-2xl leading-relaxed tracking-wide ${textColor} mb-8 pt-4 px-4 md:px-2`}>
                    &quot;{testimonials[currentIndex].quote}&quot;
                  </div>
                  
                  {/* Closing quote */}
                  {showQuoteSymbol && (
                    <div className="absolute bottom-0 right-0 opacity-40 transform rotate-180">
                      <Quote 
                        size={32} 
                        color={accentColor}
                      />
                    </div>
                  )}
                </div>
                
                {/* Author info */}
                <div className="flex flex-wrap items-center mt-6 md:mt-4">
                  <div className="relative h-14 w-14 md:h-12 md:w-12 rounded-full overflow-hidden border-2 flex-shrink-0 shadow-md" style={{ borderColor: accentColor }}>
                    <div className="absolute inset-0 flex items-center justify-center">
                      <Image
                        src={testimonials[currentIndex].image}
                        alt={testimonials[currentIndex].name}
                        width={100}
                        height={100}
                        className="min-w-full min-h-full object-cover"
                        style={{ objectPosition: '50% 30%' }}
                        priority
                      />
                    </div>
                  </div>
                  <div className="ml-4 md:ml-3 overflow-hidden w-full sm:w-auto sm:min-w-0 sm:flex-1 mt-2 sm:mt-0">
                    <div className={`font-medium text-lg md:text-base`} style={{ color: accentColor }}>
                      {testimonials[currentIndex].name}
                    </div>
                    <div className={`flex flex-col sm:flex-row sm:items-center`}>
                      <span className={`text-sm truncate ${textSecondaryColor}`}>
                        {testimonials[currentIndex].position}
                      </span>
                      <span className={`hidden sm:block ${textSecondaryColor} mx-2`}>•</span>
                      <span className={`text-sm italic truncate ${textSecondaryColor}`}>
                        {testimonials[currentIndex].company}
                      </span>
                    </div>
                  </div>
                </div>
              </motion.div>
            </AnimatePresence>
          </div>

          {/* Navigation Buttons */}
          <button
            onClick={handlePrev}
            className={`absolute left-1 md:left-2 top-1/2 -translate-y-1/2 h-9 w-9 md:h-10 md:w-10 rounded-full ${navBtnBg} shadow-lg flex items-center justify-center z-10 hover:scale-110 transition-transform duration-200`}
            aria-label="Previous testimonial"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className={`h-4 w-4 md:h-5 md:w-5 ${navBtnColor}`}
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </button>
          
          <button
            onClick={handleNext}
            className={`absolute right-1 md:right-2 top-1/2 -translate-y-1/2 h-9 w-9 md:h-10 md:w-10 rounded-full ${navBtnBg} shadow-lg flex items-center justify-center z-10 hover:scale-110 transition-transform duration-200`}
            aria-label="Next testimonial"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className={`h-4 w-4 md:h-5 md:w-5 ${navBtnColor}`}
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </button>
        </div>

        {/* Indicator dots - Removed */}
        {/*
        <div className="flex justify-center mt-6 space-x-1.5">
          {testimonials.map((_, index) => (
            <button
              key={index}
              onClick={() => {
                setDirection(index > currentIndex ? 1 : -1);
                setCurrentIndex(index);
              }}
              className={`w-2 h-2 rounded-full transition-all duration-300 ${
                index === currentIndex
                  ? 'scale-125'
                  : 'opacity-50'
              }`}
              style={{
                backgroundColor: index === currentIndex ? accentColor : darkMode ? '#4B5563' : '#D1D5DB'
              }}
              aria-label={`Go to testimonial ${index + 1}`}
            />
          ))}
        </div>
        */}
      </div>
    </div>
  );
};

export default TestimonialDisplay; 